import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Globe, Info } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  AIPrefilledField,
  AISuggestedBadge,
  CollapsibleSection,
  CountryPicker,
  MultiCountryPicker,
  type AIConfidence,
} from '../../../shared/components/common';
import {
  CUSTOMER_TYPES,
  SIZE_BANDS,
  INDUSTRY_OPTIONS,
  BUSINESS_TYPES,
  type CustomerType,
  type SizeBand,
  type SellsFlag,
  type SellsFlags,
} from './options';

export interface CompanyFormState {
  name: string;
  website: string;
  country: string | null;
  industry: string;
  customerTypes: CustomerType[];
  sizeBand: SizeBand | null;
  description: string;
  logoFileId: string | null;
  brandColor: string | null;
  /** Business-model flags (what the company sells) — drives products_tenant_settings.sells_*. */
  sellsFlags: SellsFlags;
  /** ISO country codes the tenant operates in (ignored when isGlobalOperation). */
  operatingCountries: string[];
  isGlobalOperation: boolean;
}

export type CompanyField =
  | 'name'
  | 'website'
  | 'country'
  | 'industry'
  | 'customerTypes'
  | 'sizeBand'
  | 'sellsFlags'
  | 'description';

export type CompanyConfidence = Partial<Record<CompanyField, AIConfidence>>;

interface CompanyStepProps {
  value: CompanyFormState;
  onChange: (patch: Partial<CompanyFormState>) => void;
  confidence: CompanyConfidence;
  onClearConfidence: (field: CompanyField) => void;
  /** Enrichment returned nothing — say so instead of degrading silently. */
  prefillFailed: boolean;
}

/**
 * Placeholder for the step body while the prefill poll runs. Without it the
 * 280px step area sits empty under the AIProcessingBanner and the card visibly
 * jumps when the form lands.
 */
export function CompanyStepSkeleton() {
  return (
    <div className="space-y-6" aria-hidden="true">
      <div className="flex flex-col items-center gap-3">
        <Skeleton className="h-16 w-16 rounded-xl" shimmer />
        <Skeleton className="h-3 w-48" shimmer />
      </div>
      {[0, 1, 2].map((row) => (
        <div key={row} className="space-y-1.5">
          <Skeleton className="h-3.5 w-28" shimmer />
          <Skeleton className="h-10 w-full" shimmer />
        </div>
      ))}
    </div>
  );
}

// Data default for the brand-color picker (not a theme token). `<input type="color">`
// requires a literal hex value, and this is PanLuma's documented accent blue.
// eslint-disable-next-line no-restricted-syntax -- color-picker default must be a hex literal
const DEFAULT_BRAND_COLOR = '#3b82f6';

export function CompanyStep({
  value,
  onChange,
  confidence,
  onClearConfidence,
  prefillFailed,
}: CompanyStepProps) {
  const { t } = useTranslation(['user_management', 'common']);

  const initial = value.name.trim().charAt(0).toUpperCase() || '?';
  const swatchColor = value.brandColor || DEFAULT_BRAND_COLOR;
  // Open "More details" when enrichment already answered something in there —
  // otherwise a prefilled size band or description would hide behind a chevron.
  // Controlled rather than `defaultOpen` because prefill can land *after* this
  // step has mounted (the poll outlives the spinner). Once the user works the
  // chevron themselves their choice stands.
  const moreDetailsPrefilled = Boolean(value.sizeBand || value.description.trim());
  const [moreDetailsOpen, setMoreDetailsOpen] = useState(moreDetailsPrefilled);
  const moreDetailsToggled = useRef(false);

  useEffect(() => {
    if (!moreDetailsToggled.current && moreDetailsPrefilled) setMoreDetailsOpen(true);
  }, [moreDetailsPrefilled]);

  const toggleCustomerType = (type: CustomerType) => {
    onClearConfidence('customerTypes');
    onChange({
      customerTypes: value.customerTypes.includes(type)
        ? value.customerTypes.filter((c) => c !== type)
        : [...value.customerTypes, type],
    });
  };

  const selectSizeBand = (band: SizeBand) => {
    onClearConfidence('sizeBand');
    onChange({ sizeBand: value.sizeBand === band ? null : band });
  };

  const toggleSellsFlag = (flag: SellsFlag) => {
    onClearConfidence('sellsFlags');
    onChange({ sellsFlags: { ...value.sellsFlags, [flag]: !value.sellsFlags[flag] } });
  };

  return (
    <div className="space-y-6">
      {prefillFailed && (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('setupWizardV2.company.prefillFailed')}</span>
        </div>
      )}

      {/* Brand identity: logo tile (brand-color swatch + initial) */}
      <div className="flex flex-col items-center gap-3 text-center">
        <label className="relative cursor-pointer" title={t('setupWizardV2.company.brandColorTitle')}>
          <span
            className="flex h-16 w-16 items-center justify-center rounded-xl text-2xl font-bold text-white shadow-sm ring-1 ring-border"
            style={{ backgroundColor: swatchColor }}
            aria-hidden="true"
          >
            {initial}
          </span>
          <input
            type="color"
            value={swatchColor}
            onChange={(e) => onChange({ brandColor: e.target.value })}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            aria-label={t('setupWizardV2.company.brandColorTitle')}
          />
        </label>
        <p className="text-xs text-muted-foreground">{t('setupWizardV2.company.brandColorHint')}</p>
      </div>

      {/* Company name */}
      <div className="space-y-1.5">
        <Label htmlFor="v2-company-name">{t('setupWizardV2.company.nameLabel')}</Label>
        <AIPrefilledField
          confidence={confidence.name ?? 'high'}
          onConfirm={() => onClearConfidence('name')}
        >
          <Input
            id="v2-company-name"
            value={value.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder={t('setupWizardV2.company.namePlaceholder')}
            autoComplete="organization"
          />
        </AIPrefilledField>
      </div>

      {/* Website */}
      <div className="space-y-1.5">
        <Label htmlFor="v2-company-website">{t('setupWizardV2.company.websiteLabel')}</Label>
        <AIPrefilledField
          confidence={confidence.website ?? 'high'}
          onConfirm={() => onClearConfidence('website')}
        >
          <Input
            id="v2-company-website"
            type="url"
            value={value.website}
            onChange={(e) => onChange({ website: e.target.value })}
            placeholder={t('setupWizardV2.company.websitePlaceholder')}
            autoComplete="url"
          />
        </AIPrefilledField>
      </div>

      {/* Country */}
      <div className="space-y-1.5">
        <Label>{t('setupWizardV2.company.countryLabel')}</Label>
        <AIPrefilledField
          confidence={confidence.country ?? 'high'}
          onConfirm={() => onClearConfidence('country')}
        >
          <CountryPicker
            value={value.country}
            onChange={(code) => onChange({ country: code })}
            placeholder={t('setupWizardV2.company.countryPlaceholder')}
            allowClear
          />
        </AIPrefilledField>
      </div>

      {/* Industry — free text, with one-tap fallback chips when empty (low confidence) */}
      <div className="space-y-2">
        <Label htmlFor="v2-company-industry">{t('setupWizardV2.company.industryLabel')}</Label>
        <AIPrefilledField
          confidence={confidence.industry ?? 'high'}
          onConfirm={() => onClearConfidence('industry')}
        >
          <Input
            id="v2-company-industry"
            value={value.industry}
            onChange={(e) => onChange({ industry: e.target.value })}
            placeholder={t('setupWizardV2.company.industryPlaceholder')}
          />
        </AIPrefilledField>
        {value.industry.trim().length === 0 && (
          <div className="flex flex-wrap gap-2 pt-0.5">
            {INDUSTRY_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                type="button"
                variant="outline"
                size="sm"
                className="h-11"
                onClick={() => {
                  onClearConfidence('industry');
                  onChange({ industry: opt.value });
                }}
              >
                {t(opt.labelKey)}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Business type — what the company sells (sells_* flags). Derived from
          industry where possible (pre-selected), user confirms/adjusts. */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label>{t('setupWizardV2.company.businessTypeLabel')}</Label>
          {confidence.sellsFlags && <AISuggestedBadge confidence={confidence.sellsFlags} />}
        </div>
        <p className="text-xs text-muted-foreground">{t('setupWizardV2.company.businessTypeHint')}</p>
        <div className="flex flex-wrap gap-2">
          {BUSINESS_TYPES.map((bt) => {
            const selected = value.sellsFlags[bt.flag];
            return (
              <Button
                key={bt.flag}
                type="button"
                variant={selected ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleSellsFlag(bt.flag)}
                className="h-11 gap-1.5"
              >
                {selected && <Check className="h-3.5 w-3.5" />}
                {t(`common:businessClassification.businessModel.${bt.modelValue}`)}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Customer types */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label>{t('setupWizardV2.company.customerTypesLabel')}</Label>
          {confidence.customerTypes && <AISuggestedBadge confidence={confidence.customerTypes} />}
        </div>
        <div className="flex flex-wrap gap-2">
          {CUSTOMER_TYPES.map((type) => {
            const selected = value.customerTypes.includes(type.value);
            return (
              <Button
                key={type.value}
                type="button"
                variant={selected ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleCustomerType(type.value)}
                className="h-11 gap-1.5"
              >
                {selected && <Check className="h-3.5 w-3.5" />}
                {t(`setupWizardV2.customerTypes.${type.value}`)}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Secondary, always-optional detail — folded away so the step stays short.
          Opens by default when enrichment already answered something inside. */}
      <CollapsibleSection
        label={t('setupWizardV2.company.moreDetailsLabel')}
        summary={t('setupWizardV2.company.moreDetailsSummary')}
        open={moreDetailsOpen}
        onOpenChange={(open) => {
          moreDetailsToggled.current = true;
          setMoreDetailsOpen(open);
        }}
        contentClassName="space-y-6 pt-4"
      >
        {/* Size band */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label>{t('setupWizardV2.company.sizeBandLabel')}</Label>
            {confidence.sizeBand && <AISuggestedBadge confidence={confidence.sizeBand} />}
          </div>
          <div className="flex flex-wrap gap-2">
            {SIZE_BANDS.map((band) => {
              const selected = value.sizeBand === band.value;
              return (
                <Button
                  key={band.value}
                  type="button"
                  variant={selected ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => selectSizeBand(band.value)}
                  className={cn('h-11', selected && 'gap-1.5')}
                >
                  {selected && <Check className="h-3.5 w-3.5" />}
                  {t(`setupWizardV2.sizeBands.${band.value}`)}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Operating countries + "global" toggle. Defaults to the HQ country; the
            country picker is hidden when "operate globally" is on. */}
        <div className="space-y-2">
          <Label>{t('setupWizardV2.company.operatingCountriesLabel')}</Label>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div className="flex items-center gap-2.5">
              <Globe className="h-5 w-5 text-muted-foreground" />
              <Label htmlFor="v2-global-toggle" className="cursor-pointer font-normal">
                {t('setupWizardV2.company.operateGlobally')}
              </Label>
            </div>
            <Switch
              id="v2-global-toggle"
              checked={value.isGlobalOperation}
              onCheckedChange={(checked) => onChange({ isGlobalOperation: checked })}
            />
          </div>
          {!value.isGlobalOperation && (
            <MultiCountryPicker
              value={value.operatingCountries}
              onChange={(codes) => onChange({ operatingCountries: codes })}
              placeholder={t('setupWizardV2.company.operatingCountriesPlaceholder')}
            />
          )}
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label htmlFor="v2-company-description">
            {t('setupWizardV2.company.descriptionLabel')}
          </Label>
          <AIPrefilledField
            confidence={confidence.description ?? 'high'}
            onConfirm={() => onClearConfidence('description')}
          >
            <Textarea
              id="v2-company-description"
              value={value.description}
              onChange={(e) => onChange({ description: e.target.value })}
              placeholder={t('setupWizardV2.company.descriptionPlaceholder')}
              rows={3}
            />
          </AIPrefilledField>
        </div>
      </CollapsibleSection>
    </div>
  );
}
